import { useCallback, useState } from 'react';
import { Box, Button, ButtonBase, Checkbox, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { Field, HURDLR_SERIF, SectionHeading, UnderlineInput, UnderlineSelect } from './fields';
import { computeTotals, currencyExact, lineAmount, newDraft, nextInvoiceNumber } from './format';
import { clearDraft, saveDraft, useInvoicingSession } from './invoicingStore';
import InvoiceDateField from './InvoiceDateField';
import InvoicePageLayout from './InvoicePageLayout';
import LineItemsEditor, { newLineItem } from './LineItemsEditor';
import { Client, DiscountMode, Invoice, InvoiceDraft, LineItem, Sender } from './types';

import PlusCircleIcon from 'modules/common/components/Icons/PlusCircleIcon';
import QuestionMarkCircleIcon from 'modules/common/components/Icons/QuestionMarkCircleIcon';

const MESSAGE_LIMIT = 800;

const REMINDERS_TOOLTIP =
    'Collective will automatically send payment reminders to your client. You can change the frequency of the reminders in Invoice Settings.';

const TERMS_OPTIONS: { value: InvoiceDraft['terms']; label: string }[] = [
    { value: 'due_now', label: 'Due Now' },
    { value: 'due_10', label: 'Due in 10 Days' },
    { value: 'due_15', label: 'Due in 15 Days' },
    { value: 'due_30', label: 'Due in 30 Days' },
];

const FREQUENCY_OPTIONS: { value: InvoiceDraft['frequency']; label: string }[] = [
    { value: 'one_time', label: 'One-Time Invoice' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
];

interface DiscountModeButtonProps {
    mode: DiscountMode;
    label: string;
    selected: boolean;
    onSelect: (mode: DiscountMode) => void;
}

// The real tool's small square % / $ switch: the chosen one is filled.
function DiscountModeButton({ mode, label, selected, onSelect }: Readonly<DiscountModeButtonProps>) {
    const handleClick = useCallback(() => onSelect(mode), [mode, onSelect]);

    return (
        <ButtonBase
            aria-pressed={selected}
            aria-label={mode === 'pct' ? 'Percent discount' : 'Dollar discount'}
            onClick={handleClick}
            sx={{
                width: 24,
                height: 24,
                borderRadius: 0.5,
                border: 1,
                borderColor: selected ? 'primary.main' : 'cement.300',
                bgcolor: selected ? 'primary.main' : 'background.paper',
                color: selected ? 'primary.contrastText' : 'text.primary',
                typography: 'caption',
            }}>
            {label}
        </ButtonBase>
    );
}

interface NewInvoiceFormProps {
    clients: Client[];
    invoices: Invoice[];
    sender: Sender;
}

export default function NewInvoiceForm({ clients, invoices, sender }: Readonly<NewInvoiceFormProps>) {
    const history = useHistory();
    const { draft: savedDraft } = useInvoicingSession();
    // Coming Back from the review step restores what was entered; otherwise start a fresh invoice.
    const [draft, setDraft] = useState<InvoiceDraft>(
        () => savedDraft ?? newDraft(nextInvoiceNumber(sender.businessName, invoices))
    );

    // A restored draft with CC/BCC addresses opens with those fields showing.
    const [showCc, setShowCc] = useState(() => Boolean(draft.cc));
    const [showBcc, setShowBcc] = useState(() => Boolean(draft.bcc));

    const update = useCallback((patch: Partial<InvoiceDraft>) => setDraft((d) => ({ ...d, ...patch })), []);

    const handleClientChange = useCallback(
        (clientId: string) => {
            const client = clients.find((c) => c.id === clientId);
            setDraft((d) => ({
                ...d,
                clientId,
                clientEmail: client?.email ?? '',
                // Pre-fill the client's usual work so a live demo can go straight to Save.
                items: d.items.length || !client?.default_item ? d.items : [newLineItem(client.default_item)],
            }));
        },
        [clients]
    );
    const toggleCc = useCallback(() => setShowCc((shown) => !shown), []);
    const toggleBcc = useCallback(() => setShowBcc((shown) => !shown), []);
    const handleItemsChange = useCallback((items: LineItem[]) => update({ items }), [update]);
    const handleDateChange = useCallback((date: string) => update({ date }), [update]);
    const handleTermsChange = useCallback((terms: InvoiceDraft['terms']) => update({ terms }), [update]);
    const handleFrequencyChange = useCallback(
        (frequency: InvoiceDraft['frequency']) => update({ frequency }),
        [update]
    );
    const handleDiscountMode = useCallback((discountMode: DiscountMode) => update({ discountMode }), [update]);
    const handleBack = useCallback(() => {
        clearDraft();
        history.push('/bookkeeping/invoicing');
    }, [history]);
    const handleSave = useCallback(() => {
        saveDraft(draft);
        history.push('/bookkeeping/invoicing/new/review');
    }, [draft, history]);

    const { subtotal, total } = computeTotals(draft.items, draft.discount, draft.discountMode);
    const canSave = Boolean(draft.clientId) && draft.items.some((item) => lineAmount(item) > 0);
    const clientOptions = clients.map((c) => ({ value: c.id, label: c.name }));

    return (
        <InvoicePageLayout
            title="New Invoice"
            onBack={handleBack}
            next={{ label: 'Save', onClick: handleSave, disabled: !canSave }}
            main={
                <>
                    {/* Fields and CC/BCC share a bottom edge so every control sits on one line. */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} alignItems={{ sm: 'flex-end' }}>
                        <Field label="Client" flex={1}>
                            <UnderlineSelect
                                label="Client"
                                value={draft.clientId}
                                options={clientOptions}
                                onChange={handleClientChange}
                                placeholder="Choose your client"
                            />
                        </Field>
                        <Field label="Client Email" flex={1}>
                            <UnderlineInput
                                placeholder="Enter client email"
                                value={draft.clientEmail}
                                onChange={(e) => update({ clientEmail: e.target.value })}
                                inputProps={{ 'aria-label': 'Client Email' }}
                            />
                        </Field>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="text"
                                color="primary"
                                size="small"
                                aria-pressed={showCc}
                                onClick={toggleCc}>
                                CC
                            </Button>
                            <Button
                                variant="text"
                                color="primary"
                                size="small"
                                aria-pressed={showBcc}
                                onClick={toggleBcc}>
                                BCC
                            </Button>
                        </Stack>
                    </Stack>
                    {showCc && (
                        <Box sx={{ mt: 3 }}>
                            <Field label="CC">
                                <UnderlineInput
                                    autoFocus
                                    placeholder="Enter cc emails (optional)"
                                    value={draft.cc}
                                    onChange={(e) => update({ cc: e.target.value })}
                                    inputProps={{ 'aria-label': 'CC' }}
                                />
                            </Field>
                        </Box>
                    )}
                    {showBcc && (
                        <Box sx={{ mt: 3 }}>
                            <Field label="BCC">
                                <UnderlineInput
                                    autoFocus
                                    placeholder="Enter bcc emails (optional)"
                                    value={draft.bcc}
                                    onChange={(e) => update({ bcc: e.target.value })}
                                    inputProps={{ 'aria-label': 'BCC' }}
                                />
                            </Field>
                        </Box>
                    )}

                    <Box sx={{ mt: 7 }}>
                        <SectionHeading>Items</SectionHeading>
                        <LineItemsEditor
                            items={draft.items}
                            hasClient={Boolean(draft.clientId)}
                            onChange={handleItemsChange}
                        />
                    </Box>

                    <Box sx={{ mt: 7 }}>
                        <SectionHeading>Attachments</SectionHeading>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<PlusCircleIcon fontSize="small" />}>
                            Attach File
                        </Button>
                    </Box>

                    <Box sx={{ mt: 8 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption">Message</Typography>
                            <Typography variant="caption">
                                {Math.max(0, MESSAGE_LIMIT - draft.message.length)}
                            </Typography>
                        </Stack>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            value={draft.message}
                            onChange={(e) => update({ message: e.target.value.slice(0, MESSAGE_LIMIT) })}
                            inputProps={{ 'aria-label': 'Message' }}
                        />
                    </Box>
                </>
            }
            side={
                <>
                    <Box sx={{ width: '60%', mb: 5 }}>
                        <Field label="Invoice Number">
                            <UnderlineInput
                                placeholder="Enter invoice name..."
                                value={draft.number}
                                onChange={(e) => update({ number: e.target.value })}
                                inputProps={{ 'aria-label': 'Invoice Number' }}
                            />
                        </Field>
                    </Box>

                    <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
                        <Field label="Invoice Date" flex={1}>
                            <InvoiceDateField value={draft.date} onChange={handleDateChange} />
                        </Field>
                        <Field label="Terms" flex={1}>
                            <UnderlineSelect
                                label="Terms"
                                value={draft.terms}
                                options={TERMS_OPTIONS}
                                onChange={handleTermsChange}
                            />
                        </Field>
                    </Stack>

                    <Box sx={{ mb: 3 }}>
                        <Field label="Frequency">
                            <UnderlineSelect
                                label="Frequency"
                                value={draft.frequency}
                                options={FREQUENCY_OPTIONS}
                                onChange={handleFrequencyChange}
                            />
                        </Field>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox
                            size="small"
                            checked={draft.reminders}
                            onChange={(e) => update({ reminders: e.target.checked })}
                            sx={{ p: 0 }}
                            inputProps={{ 'aria-label': 'Send payment reminders' }}
                        />
                        <Typography variant="body2">Send payment reminders</Typography>
                        <Tooltip title={REMINDERS_TOOLTIP} placement="top" arrow>
                            <Box
                                component="span"
                                tabIndex={0}
                                aria-label="About payment reminders"
                                sx={{ display: 'flex' }}>
                                <QuestionMarkCircleIcon fontSize="small" />
                            </Box>
                        </Tooltip>
                    </Stack>

                    {/* The totals sit at the foot of the panel, as in the real tool. */}
                    <Box sx={{ mt: 'auto', pt: 8 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Typography variant="body2">Subtotal</Typography>
                            <Typography variant="body2">{currencyExact(subtotal)}</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography variant="body2">Discount</Typography>
                            <Stack direction="row" spacing={0.5}>
                                <DiscountModeButton
                                    mode="pct"
                                    label="%"
                                    selected={draft.discountMode === 'pct'}
                                    onSelect={handleDiscountMode}
                                />
                                <DiscountModeButton
                                    mode="usd"
                                    label="$"
                                    selected={draft.discountMode === 'usd'}
                                    onSelect={handleDiscountMode}
                                />
                            </Stack>
                            <UnderlineInput
                                type="number"
                                placeholder="Discount"
                                value={draft.discount}
                                onChange={(e) => update({ discount: e.target.value })}
                                inputProps={{ 'aria-label': 'Discount', min: 0, style: { textAlign: 'right' } }}
                            />
                        </Stack>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="baseline"
                            sx={{ borderTop: 1, borderColor: 'cement.300', mt: 3, pt: 3 }}>
                            <Typography variant="h5" sx={{ fontFamily: HURDLR_SERIF, fontWeight: 400 }}>
                                Total
                            </Typography>
                            <Typography variant="subtitle1">{currencyExact(total)}</Typography>
                        </Stack>
                    </Box>
                </>
            }
        />
    );
}
